import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Button, IconButton, Badge } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, DataTable, type ColumnDef } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Shield, Zap, Settings, Trash2, Pencil } from 'lucide-react';
import type { ServiceSla, SlaRule } from '@/api/services';
import { useServiceSla } from '@/api/services';
import { useDeleteServiceSla } from '@/api/services-mutations';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { SLA_TIER_TONE, formatMinutes, resolveTier } from './sla-constants';
import { SlaTemplatePickerDialog } from './sla-template-picker-dialog';
import { SlaManualConfigDialog } from './sla-manual-config-dialog';

const muted = { color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' } as const;

function SetupCard({ icon, title, desc, onClick }: { icon: React.ReactNode; title: string; desc: string; onClick: () => void }) {
  return (
    <Card flat onClick={onClick} style={{ cursor: 'pointer' }}>
      <CardBody style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'flex-start' }}>
        <span style={{ color: 'var(--vn-primary-text)' }}>{icon}</span>
        <span style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>{title}</span>
          <span style={muted}>{desc}</span>
        </span>
      </CardBody>
    </Card>
  );
}

/**
 * SLA config block — "Service SLA" header with either the setup options (apply
 * template / manual config) when none is attached, or the configured terms
 * (tier badge, metric grid, rules table, effective window) with Edit / Remove.
 */
export function SlaConfigSection({ id }: { id: number }) {
  const toast = useToast();
  const sla = useServiceSla(id);
  const del = useDeleteServiceSla(id);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (sla.isLoading) return <TabLoading rows={3} />;

  const s = sla.data?.result as ServiceSla | null | undefined;

  const doDelete = () => {
    del.mutate(undefined, {
      onSuccess: () => { toast.success('SLA config removed'); setConfirmDelete(false); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to remove SLA config'),
    });
  };

  return (
    <Card flat>
      <CardHeader
        actions={
          s ? (
            <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
              <Button size="sm" variant="secondary" onClick={() => setManualOpen(true)}><Pencil size={14} /> Edit</Button>
              <IconButton aria-label="Remove SLA" variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}><Trash2 size={15} /></IconButton>
            </div>
          ) : undefined
        }
      >
        <CardTitle>Service SLA</CardTitle>
      </CardHeader>
      <CardBody>
        {!s ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <span style={muted}>No SLA is configured for this service.</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--vn-space-3)' }}>
              <SetupCard icon={<Zap size={22} />} title="Apply template" desc="Pick from the ready-made SLA tiers (Bronze, Silver, Gold, Platinum)." onClick={() => setPickerOpen(true)} />
              <SetupCard icon={<Settings size={22} />} title="Manual config" desc="Set the SLA terms directly to fit the customer's requirements." onClick={() => setManualOpen(true)} />
            </div>
          </div>
        ) : (
          <ConfiguredView s={s} />
        )}
      </CardBody>

      <SlaTemplatePickerDialog serviceId={id} open={pickerOpen} onOpenChange={setPickerOpen} />
      <SlaManualConfigDialog serviceId={id} existing={s} open={manualOpen} onOpenChange={setManualOpen} />
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={(o) => (o ? undefined : setConfirmDelete(false))}
        title="Remove SLA config"
        description="Remove the SLA config from this service? This action cannot be undone."
        confirmLabel="Remove"
        destructive
        loading={del.isPending}
        onConfirm={doDelete}
      />
    </Card>
  );
}

const ruleColumns: ColumnDef<SlaRule, unknown>[] = [
  { id: 'type', header: 'Ticket type', cell: ({ row }) => row.original.ticket_type_group === 'default' ? 'Default' : row.original.ticket_type_key || row.original.ticket_type_group },
  { id: 'priority', header: 'Priority', cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.priority || '—'}</span> },
  { id: 'response', header: 'Response', cell: ({ row }) => formatMinutes(row.original.response_time_minutes) },
  { id: 'resolution', header: 'Resolution', cell: ({ row }) => formatMinutes(row.original.resolution_time_minutes) },
];

function ConfiguredView({ s }: { s: ServiceSla }) {
  const tier = resolveTier(s.template_tier);
  const rules = (s.effective_sla_rules?.length ? s.effective_sla_rules : s.sla_rules) ?? [];
  const defRule = rules.find((r) => r.ticket_type_group === 'default') ?? rules[0];
  const uptime = s.uptime_guarantee ?? s.effective_uptime_guarantee;
  const downtime = s.downtime_allowance_minutes ?? s.effective_downtime_allowance;
  const response = defRule?.response_time_minutes ?? s.template_default_response_time;
  const resolution = defRule?.resolution_time_minutes ?? s.template_default_resolution_time;
  const bizHours = s.business_hours_only ?? s.effective_business_hours_only;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <Shield size={18} style={{ color: 'var(--vn-primary-text)' }} />
        <span style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>{s.template_name || 'Manual config'}</span>
        {tier ? <Badge tone={SLA_TIER_TONE[tier] ?? 'neutral'} appearance="subtle" size="sm">{tier.toUpperCase()}</Badge> : null}
        <Badge tone={s.is_active ? 'success' : 'neutral'} appearance="subtle" size="sm">{s.is_active ? 'Active' : 'Inactive'}</Badge>
      </div>

      <StatGroup columns={4}>
        <Stat label="Uptime" value={uptime != null ? `${uptime}%` : '—'} dotColor="var(--vn-success-solid)" />
        <Stat label="Downtime" value={formatMinutes(downtime)} dotColor="var(--vn-warning-solid)" />
        <Stat label="Response" value={formatMinutes(response)} dotColor="var(--vn-info-solid)" />
        <Stat label="Resolution" value={formatMinutes(resolution)} dotColor="var(--vn-accent-solid)" />
      </StatGroup>

      {rules.length > 0 && (
        <DataTable
          columns={ruleColumns}
          data={rules}
          getRowId={(_r, i) => String(i)}
        />
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-3)', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', paddingTop: 'var(--vn-space-2)', borderTop: '1px solid var(--vn-border-subtle)' }}>
        <span>Effective: {s.effective_from || '—'}{s.effective_to ? ` → ${s.effective_to}` : ''}</span>
        <span>{bizHours ? 'Business hours' : '24/7'}</span>
      </div>
    </div>
  );
}
